// План строительства в сетевой игре: строения, которые игрок размечает на поле
// в чужой ход фазы строительства (см. getConstructionPlanContext). Живет только
// в приватном состоянии клиента — экшены плана с noServerSync и на сервер не
// уходят. В начале собственного хода строительства план применяется
// настоящими действиями строительства (см. constructionPlanMiddleware).
//
// Общего состояния партии редьюсер не видит: отмена запланированного по чужой
// стройке и сброс при смене контекста (фаза, раунд, конец партии) — в
// constructionPlanMiddleware. Здесь — только операции над самим планом и сброс
// при выходе из матча
export const CONSTRUCTION_PLAN_INITIAL_STATE = Object.freeze({
  // [{hexKey, token}] в порядке планирования. Порядок важен: от него зависят
  // цены (бесплатная первая ферма аграризма) и строения на запланированных
  // кварталах
  entries: [],
  // Строение, выбранное для планирования. Не selectedToken: тот сбрасывается
  // на каждом COMPLETE_PHASE и переключается ботами, чьи ходы исполняет клиент
  brushToken: null,
  // Ключ хода (getConstructionPlanTurnKey), в начале которого план уже
  // применен: защита от повторного применения после переподключения и LOAD_STATE
  appliedTurnKey: null,
  // Чекбокс «Автоматически завершить ход»: в начале своего хода план
  // применяется и ход сразу завершается. Выставляется только проверенному
  // плану (validateConstructionPlan)
  isAutoCompleteTurn: false,
  // Ячейки запланированных строений, не прошедших последнюю проверку плана, —
  // красные молотки. Как и у строений своего хода, подсветка — итог проверки:
  // игрок поменял план — она устарела и снимается
  invalidHexKeys: [],
});

// Оставить подсветку только на ячейках, где что-то еще запланировано: снятое
// с плана чужой стройкой подсвечивать уже нечего
const keepInvalidHexKeysOfEntries = (invalidHexKeys, entries) => {
  if (invalidHexKeys.length === 0) {
    return invalidHexKeys;
  }
  const kept = invalidHexKeys.filter((hexKey) => entries.some((entry) => entry.hexKey === hexKey));
  return kept.length === invalidHexKeys.length ? invalidHexKeys : kept;
};

const withEntries = (state, entries, {isPlayerChange}) => {
  const invalidHexKeys = state.invalidHexKeys || [];
  return {
    ...state,
    entries,
    invalidHexKeys: isPlayerChange
      ? (invalidHexKeys.length === 0 ? invalidHexKeys : [])
      : keepInvalidHexKeysOfEntries(invalidHexKeys, entries),
  };
};

const constructionPlanReducer = (state = CONSTRUCTION_PLAN_INITIAL_STATE, action) => {
  switch (action.type) {
    case 'SET_CONSTRUCTION_PLAN_BRUSH': {
      const token = action.payload?.token || null;
      return state.brushToken === token ? state : {...state, brushToken: token};
    }

    case 'ADD_CONSTRUCTION_PLAN_ENTRY': {
      const {hexKey, token} = action.payload || {};
      if (!hexKey || !token) {
        return state;
      }
      return withEntries(state, [...state.entries, {hexKey, token}], {isPlayerChange: true});
    }

    case 'REMOVE_CONSTRUCTION_PLAN_ENTRY': {
      const hexKey = action.payload?.hexKey;
      let index = -1;
      for (let i = state.entries.length - 1; i >= 0; i--) {
        if (state.entries[i].hexKey === hexKey) {
          index = i;
          break;
        }
      }
      if (index === -1) {
        return state;
      }
      return withEntries(state, state.entries.filter((_, entryIndex) => entryIndex !== index), {isPlayerChange: true});
    }

    case 'REMOVE_CONSTRUCTION_PLAN_ENTRIES_AT': {
      const hexKeys = new Set(action.payload?.hexKeys || []);
      const entries = state.entries.filter((entry) => !hexKeys.has(entry.hexKey));
      return entries.length === state.entries.length ? state : withEntries(state, entries, {isPlayerChange: false});
    }

    case 'REMOVE_CONSTRUCTION_PLAN_ENTRIES': {
      const removedKeys = new Set((action.payload?.entries || []).map(({hexKey, token}) => `${hexKey}|${token}`));
      const entries = state.entries.filter(({hexKey, token}) => !removedKeys.has(`${hexKey}|${token}`));
      return entries.length === state.entries.length ? state : withEntries(state, entries, {isPlayerChange: false});
    }

    case 'REMOVE_LAST_CONSTRUCTION_PLAN_ENTRY':
      return state.entries.length === 0
        ? state
        : withEntries(state, state.entries.slice(0, -1), {isPlayerChange: true});

    case 'SET_CONSTRUCTION_PLAN_AUTO_COMPLETE_TURN': {
      const isAutoCompleteTurn = Boolean(action.payload?.isEnabled);
      return Boolean(state.isAutoCompleteTurn) === isAutoCompleteTurn ? state : {...state, isAutoCompleteTurn};
    }

    case 'SET_CONSTRUCTION_PLAN_INVALID_HEX_KEYS': {
      const invalidHexKeys = [...new Set(action.payload?.hexKeys || [])];
      const currentHexKeys = state.invalidHexKeys || [];
      const isSame = invalidHexKeys.length === currentHexKeys.length
        && invalidHexKeys.every((hexKey, index) => hexKey === currentHexKeys[index]);
      return isSame ? state : {...state, invalidHexKeys};
    }

    // Чекбокс снимается вместе с планом: сброс идет при смене рамки плана
    // (раунд, фаза, лобби) и когда планировать больше нечего — к следующей
    // фазе строительства игрок решает заново
    case 'CLEAR_CONSTRUCTION_PLAN':
      return state.entries.length === 0 && state.brushToken === null
        && !state.isAutoCompleteTurn && (state.invalidHexKeys || []).length === 0
        ? state
        : {...state, entries: [], brushToken: null, isAutoCompleteTurn: false, invalidHexKeys: []};

    case 'MARK_CONSTRUCTION_PLAN_APPLIED':
      return {
        ...CONSTRUCTION_PLAN_INITIAL_STATE,
        appliedTurnKey: action.payload?.turnKey || null,
      };

    case 'RESTORE_CONSTRUCTION_PLAN': {
      const entries = (action.payload?.entries || []).filter((entry) => entry && entry.hexKey && entry.token);
      return {
        ...CONSTRUCTION_PLAN_INITIAL_STATE,
        entries: entries.map(({hexKey, token}) => ({hexKey, token})),
        isAutoCompleteTurn: Boolean(action.payload?.isAutoCompleteTurn),
      };
    }

    // Выход из матча и старт другой партии: план прошлой игры не нужен.
    // RESET_STATE сбрасывает срез сам (privateStateReducer)
    case 'LEAVE_LOBBY':
    case 'SET_GAME_TYPE':
    case 'START_GAME':
      return state === CONSTRUCTION_PLAN_INITIAL_STATE ? state : CONSTRUCTION_PLAN_INITIAL_STATE;

    default:
      return state;
  }
};

export default constructionPlanReducer;
